/**
 * Spare Parts compatibility — driven by product descriptions.
 *
 * Nearly all spare parts use:
 *   Genuine MICROTEC replacement part.
 *   Compatible with:
 *    T1620-AH/T1620-A2/T1620-ASP
 *    X4/6/8
 *    XC-25/37
 *    XM-15
 *
 * Matching is SKU/product-number based (with shorthand expansion + aliases).
 * Product tags are never used for fitment.
 */
(function (global) {
  'use strict';

  var SERIES = ['VECTOR', 'TITAN', 'ATLAS', 'APEX', 'AEGIS'];

  /**
   * Known storefront SKU aliases (parts docs vs live product numbers).
   * Keys and values are compact uppercase (no hyphens).
   */
  var SKU_ALIAS_GROUPS = [
    // TITAN hybrid listed as AH in parts docs, sold as AS
    ['T1620AH', 'T1620AS'],
    // TITAN Pro swing-away
    ['T1620PROAS', 'T1620PROAWL'],
    // TITAN pneumatic dual-station cap combos
    ['TCAPPL2COMBO', 'TCAPAP2COMBO'],
    // Vector Pro family abbreviated VPro-AS in parts docs
    ['VPROAS', 'V1515PROAS', 'V1620PROAS', 'V1624PROAS', '90210721'],
    // APEX X6 variants
    ['X6', 'X6STANDARD', 'X6PREHEAT'],
    // Bare size token sometimes used
    ['1620AH', 'T1620AH', 'T1620AS'],
  ];

  var aliasMap = null;

  function buildAliasMap() {
    if (aliasMap) return aliasMap;
    aliasMap = {};
    SKU_ALIAS_GROUPS.forEach(function (group) {
      group.forEach(function (a) {
        aliasMap[a] = group.slice();
      });
    });
    return aliasMap;
  }

  function stripHtml(html) {
    if (!html) return '';
    return String(html)
      .replace(/<script[\s\S]*?<\/script>/gi, ' ')
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|h[1-6]|tr|ul|ol)>/gi, '\n')
      .replace(/<li[^>]*>/gi, '\n')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/g, "'");
  }

  function normalizeText(str) {
    if (!str) return '';
    return String(str)
      .replace(/[“”„"]/g, '"')
      .replace(/[‘’']/g, "'")
      .replace(/[×*]/g, 'x')
      .replace(/[–—]/g, ' ')
      .replace(/['"]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();
  }

  function compactSku(sku) {
    return normalizeText(sku).replace(/[^A-Z0-9]/g, '');
  }

  function expandAliases(compact) {
    var map = buildAliasMap();
    var c = compactSku(compact);
    if (!c) return [];
    if (map[c]) return map[c].slice();
    return [c];
  }

  /**
   * Split a Compatible-with value into raw segments.
   * Handles / , Chinese comma ， backslash, and glued tokens.
   */
  function splitCompatValue(line) {
    if (!line) return [];
    var cleaned = String(line)
      .replace(/[，、]/g, '/')
      .replace(/\\/g, '/')
      .replace(/\s+/g, ' ')
      .trim();

    // Fix glued codes only (no hyphen between): T1620A2T1620ASP → T1620A2/T1620ASP
    // Do NOT split valid SKUs like T1620-A2
    cleaned = cleaned.replace(
      /([A-Za-z]+\d+[A-Za-z0-9]*)(?=[A-Za-z]+\d)/g,
      '$1/'
    );

    return cleaned
      .split(/[\/,;]+/)
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
  }

  /**
   * Expand shorthand segments:
   *   X4/6/8     → X4, X6, X8  (when split first becomes X4, 6, 8)
   *   XC-25/37   → XC-25, XC-37
   *   AT32-AS/40 → AT32-AS, AT40-AS
   *   XC25/37    → XC25, XC37
   */
  function expandShorthandTokens(tokens) {
    var out = [];
    var prevFull = '';

    tokens.forEach(function (tok) {
      var t = tok.trim();
      if (!t) return;

      // Pure numeric / short tail after a full model
      if (/^\d{1,4}$/.test(t) && prevFull) {
        var expanded = applyNumericShorthand(prevFull, t);
        if (expanded) {
          out.push(expanded);
          // keep prevFull as original family base for next numeric (X4 → 6 → 8)
          return;
        }
      }

      out.push(t);
      prevFull = t;
    });

    return out;
  }

  function applyNumericShorthand(prevFull, num) {
    // XC-25 + 37 → XC-37 ; AT32-AS + 40 → AT40-AS ; X4 + 6 → X6
    var m = prevFull.match(/^([A-Za-z]+-?)(\d+)(.*)$/);
    if (m) {
      return m[1] + num + m[3];
    }
    m = prevFull.match(/^([A-Za-z]+)(\d+)(.*)$/);
    if (m) {
      return m[1] + num + m[3];
    }
    return '';
  }

  /**
   * Extract product-number tokens from a "Compatible with:" block.
   */
  function extractCompatibleWithTokens(text) {
    var plain = stripHtml(text);
    var tokens = [];

    // Multi-line block
    var lines = plain.split(/\r?\n/);
    var inBlock = false;
    var blockChunks = [];

    for (var i = 0; i < lines.length; i++) {
      var raw = lines[i].trim();
      if (!raw) {
        if (inBlock && blockChunks.length) break;
        continue;
      }
      if (/^compatible\s+with\s*:?\s*$/i.test(raw) || /^compatible\s+with\s*:/i.test(raw)) {
        inBlock = true;
        var after = raw.replace(/^compatible\s+with\s*:?\s*/i, '').trim();
        if (after) blockChunks.push(after);
        continue;
      }
      if (inBlock) {
        if (/^(note|genuine|specifications?|features)\b/i.test(raw)) break;
        blockChunks.push(raw);
      }
    }

    var blockText = blockChunks.join('/');

    // Single-line fallback (must have colon after "with")
    if (!blockText) {
      var collapsed = plain.replace(/\s+/g, ' ');
      var m = collapsed.match(
        /(?:^|[^A-Za-z])compatible\s+with\s*:\s*([^.]+?)(?:\.|$)/i
      );
      if (m && m[1] && !/not\s+compatible/i.test(collapsed.slice(Math.max(0, (m.index || 0) - 4), (m.index || 0) + 12))) {
        blockText = m[1].trim();
      }
    }

    if (!blockText) return [];

    var rawTokens = splitCompatValue(blockText);
    var expanded = expandShorthandTokens(rawTokens);

    expanded.forEach(function (tok) {
      var c = compactSku(tok);
      if (c.length >= 2 && tokens.indexOf(c) === -1) tokens.push(c);
      // also keep original display form? compact is enough for matching
    });

    return tokens;
  }

  function extractSeriesFromBody(text) {
    var n = normalizeText(stripHtml(text));
    var found = [];
    SERIES.forEach(function (s) {
      var re = new RegExp('(?:^|[^A-Z0-9])' + s + '(?:[^A-Z0-9]|$)');
      if (re.test(n) && found.indexOf(s) === -1) found.push(s);
    });
    return found;
  }

  function extractNegatedSeries(text) {
    var plain = stripHtml(text);
    var upper = normalizeText(plain);
    var negated = [];
    SERIES.forEach(function (s) {
      var patterns = [
        'NOT COMPATIBLE WITH ' + s,
        'NOT FOR ' + s,
        'NOT DESIGNED FOR ' + s,
      ];
      patterns.forEach(function (p) {
        if (upper.indexOf(p) !== -1 && negated.indexOf(s) === -1) negated.push(s);
      });
    });
    return negated;
  }

  /**
   * @param {string} description
   * @param {string[]|string} tags - ignored for fitment
   */
  function extractCompat(description, tags) {
    var tagList = [];
    if (Array.isArray(tags)) tagList = tags;
    else if (typeof tags === 'string' && tags) {
      tagList = tags.split(',').map(function (t) {
        return t.trim();
      });
    }

    var compatTokens = extractCompatibleWithTokens(description);
    var plain = normalizeText(stripHtml(description));
    var positiveSeries = extractSeriesFromBody(description);
    var negatedSeries = extractNegatedSeries(description);
    positiveSeries = positiveSeries.filter(function (s) {
      return negatedSeries.indexOf(s) === -1;
    });

    // Legacy models array for UI (human-readable tokens before compact)
    var models = [];
    if (compatTokens.length) {
      models = compatTokens.slice();
    }

    return {
      models: models,
      modelsNormalized: models.map(normalizeText),
      // Compact product numbers this part fits
      compatTokens: compatTokens,
      hasModelList: compatTokens.length > 0,
      hasSkuSignals: compatTokens.length > 0,
      skusInText: compatTokens.slice(),
      positiveSeries: positiveSeries,
      negatedSeries: negatedSeries,
      plain: plain,
      isUniversal: compatTokens.length === 0 && positiveSeries.length === 0,
      tags: tagList,
    };
  }

  function detectSeriesFromTitle(title) {
    var n = normalizeText(title);
    for (var i = 0; i < SERIES.length; i++) {
      if (n.indexOf(SERIES[i]) !== -1) return SERIES[i];
    }
    return '';
  }

  function seriesFromSku(sku) {
    var s = compactSku(sku);
    if (!s) return '';
    if (s.indexOf('TCAP') === 0 || (s.charAt(0) === 'T' && /\d/.test(s))) return 'TITAN';
    if (s.indexOf('VMUG') === 0 || s.indexOf('VCAP') === 0 || s.indexOf('VBALL') === 0) return 'VECTOR';
    if (s.charAt(0) === 'V' && /\d/.test(s)) return 'VECTOR';
    if (s.indexOf('90210') === 0) return 'VECTOR';
    if (s.indexOf('AT') === 0) return 'ATLAS';
    if (s.indexOf('XM') === 0 || s.indexOf('XR') === 0 || s.indexOf('XC') === 0 || s.charAt(0) === 'X')
      return 'APEX';
    return '';
  }

  function buildMachine(raw) {
    var title = raw.title || '';
    var sku = (raw.sku || '').trim();
    var series =
      detectSeriesFromTitle(title) ||
      seriesFromSku(sku) ||
      normalizeText(raw.series || '');
    if (SERIES.indexOf(series) === -1) {
      series = seriesFromSku(sku) || detectSeriesFromTitle(title);
    }

    var titleN = normalizeText(title);
    var skuC = compactSku(sku);

    return {
      id: raw.id || sku || title,
      sku: sku,
      skuCompact: skuC,
      // All compact forms that should match this machine
      matchKeys: expandAliases(skuC).concat(
        // also allow matching bare X6 for X6-Standard etc.
        skuC.indexOf('X6') === 0 && skuC !== 'X6' ? ['X6'] : []
      ),
      title: title,
      titleNormalized: titleN,
      url: raw.url || '',
      series: series,
      sizes: [],
      isPro: titleN.indexOf('PRO') !== -1,
      isMug: titleN.indexOf('MUG') !== -1 || skuC.indexOf('XM') === 0 || skuC.indexOf('VMUG') === 0,
      isCap: titleN.indexOf('CAP') !== -1 || skuC.indexOf('CAP') !== -1 || skuC.indexOf('TCAP') === 0,
      image: raw.image || '',
    };
  }

  function unique(arr) {
    var out = [];
    arr.forEach(function (x) {
      if (x && out.indexOf(x) === -1) out.push(x);
    });
    return out;
  }

  function machineMatchKeys(machine) {
    var keys = [];
    if (machine.matchKeys && machine.matchKeys.length) {
      keys = machine.matchKeys.slice();
    } else {
      keys = expandAliases(machine.skuCompact);
    }
    // X6-STANDARD also matches token X6
    if (machine.skuCompact && machine.skuCompact.indexOf('X6') === 0) {
      keys.push('X6');
    }
    // V1620PROAS also matches VPROAS family token
    if (machine.skuCompact && machine.skuCompact.indexOf('PRO') !== -1 && machine.series === 'VECTOR') {
      keys.push('VPROAS');
    }
    return unique(keys.map(compactSku).filter(Boolean));
  }

  function tokenMatchesMachine(tokenCompact, machine) {
    if (!tokenCompact || !machine) return false;
    var t = compactSku(tokenCompact);
    if (!t) return false;

    var keys = machineMatchKeys(machine);
    // Exact on any alias key
    if (keys.indexOf(t) !== -1) return true;

    // Expand token aliases and compare
    var tokenAliases = expandAliases(t);
    for (var i = 0; i < tokenAliases.length; i++) {
      if (keys.indexOf(tokenAliases[i]) !== -1) return true;
    }

    // Prefix: machine X6STANDARD starts with token X6 (length >= 2)
    // Avoid X matching everything — require token length >= 2 and not pure digit-only of length 1
    if (t.length >= 2) {
      for (var j = 0; j < keys.length; j++) {
        if (keys[j].indexOf(t) === 0 && (t.length >= 3 || /^[A-Z]+\d/.test(t))) return true;
        // token AT40AS vs machine key — already exact
      }
    }

    return false;
  }

  function findMachineByProductNumber(machines, query) {
    if (!query || !machines || !machines.length) return null;
    var qNorm = normalizeText(query);
    var qCompact = compactSku(query);

    var exact = null;
    var compactHit = null;
    for (var i = 0; i < machines.length; i++) {
      var m = machines[i];
      if (normalizeText(m.sku) === qNorm) {
        exact = m;
        break;
      }
      if (m.skuCompact && m.skuCompact === qCompact) compactHit = compactHit || m;
      // alias hit
      if (tokenMatchesMachine(qCompact, m)) compactHit = compactHit || m;
    }
    if (exact) return exact;
    if (compactHit) return compactHit;

    var prefixHits = machines.filter(function (m) {
      return (
        m.skuCompact &&
        qCompact.length >= 3 &&
        (m.skuCompact.indexOf(qCompact) === 0 || qCompact.indexOf(m.skuCompact) === 0)
      );
    });
    if (prefixHits.length === 1) return prefixHits[0];
    return null;
  }

  function filterMachinesForAutocomplete(machines, query, limit) {
    limit = limit || 8;
    if (!machines) return [];
    var q = String(query || '').trim();
    if (!q) return machines.slice(0, limit);

    var qNorm = normalizeText(q);
    var qCompact = compactSku(q);
    var scored = [];

    machines.forEach(function (m) {
      var score = 0;
      var skuN = normalizeText(m.sku);
      var titleN = m.titleNormalized || normalizeText(m.title);
      if (skuN === qNorm) score = 100;
      else if (m.skuCompact === qCompact) score = 95;
      else if (tokenMatchesMachine(qCompact, m)) score = 90;
      else if (skuN.indexOf(qNorm) === 0) score = 80;
      else if (m.skuCompact.indexOf(qCompact) === 0) score = 75;
      else if (skuN.indexOf(qNorm) !== -1) score = 60;
      else if (titleN.indexOf(qNorm) !== -1) score = 40;
      if (score > 0) scored.push({ machine: m, score: score });
    });

    scored.sort(function (a, b) {
      return b.score - a.score;
    });
    return scored.slice(0, limit).map(function (x) {
      return x.machine;
    });
  }

  /**
   * Core: does this part fit this machine?
   */
  function partMatchesMachine(compat, machine, options) {
    options = options || {};
    if (!compat || !machine) return { match: false, confidence: 'none' };

    if (machine.series && compat.negatedSeries && compat.negatedSeries.indexOf(machine.series) !== -1) {
      return { match: false, confidence: 'none' };
    }

    // Primary path: Compatible with: product numbers
    if (compat.compatTokens && compat.compatTokens.length) {
      for (var i = 0; i < compat.compatTokens.length; i++) {
        if (tokenMatchesMachine(compat.compatTokens[i], machine)) {
          return { match: true, confidence: 'high' };
        }
      }
      return { match: false, confidence: 'none' };
    }

    // Fallback: series-only body copy (rare for current spare parts)
    if (
      machine.series &&
      compat.positiveSeries &&
      compat.positiveSeries.indexOf(machine.series) !== -1
    ) {
      return { match: true, confidence: 'medium' };
    }

    if (compat.isUniversal && options.includeUniversal) {
      return { match: true, confidence: 'low' };
    }

    return { match: false, confidence: 'none' };
  }

  function machinesForPart(compat, machines, options) {
    options = options || {};
    var out = [];
    if (!machines) return out;
    machines.forEach(function (m) {
      var r = partMatchesMachine(compat, m, options);
      if (r.match) out.push({ machine: m, confidence: r.confidence });
    });
    var seen = {};
    return out.filter(function (item) {
      var key = item.machine.sku || item.machine.title;
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    });
  }

  // Back-compat exports used by older code paths
  function extractCompatibleModels(text) {
    return extractCompatibleWithTokens(text);
  }

  var api = {
    SERIES: SERIES,
    stripHtml: stripHtml,
    normalizeText: normalizeText,
    compactSku: compactSku,
    extractCompat: extractCompat,
    extractCompatibleModels: extractCompatibleModels,
    extractCompatibleWithTokens: extractCompatibleWithTokens,
    expandShorthandTokens: expandShorthandTokens,
    buildMachine: buildMachine,
    findMachineByProductNumber: findMachineByProductNumber,
    filterMachinesForAutocomplete: filterMachinesForAutocomplete,
    partMatchesMachine: partMatchesMachine,
    machinesForPart: machinesForPart,
    tokenMatchesMachine: tokenMatchesMachine,
  };

  global.MicrotecPartsCompat = api;
})(typeof window !== 'undefined' ? window : this);
